import React, { useState, useEffect } from 'react';
import './EffectsOverlay.css';

const EffectsOverlay = ({ effect, onComplete }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (effect) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        onComplete();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [effect, onComplete]);

  if (!effect || !visible) return null;

  const renderYahtzeeEffect = () => (
    <div className="yahtzee-effect">
      {/* MASSIVE confetti explosion */}
      <div className="confetti-cats">
        {Array.from({ length: 100 }, (_, i) => (
          <div key={i} className={`confetti-cat confetti-${i % 20}`}>
            <img 
              src="/cat-images/cat-high-5.png" 
              alt="Celebrating cat" 
              className="celebration-cat"
            />
          </div>
        ))}
      </div>
      
      {/* INTENSE disco lights covering entire screen */}
      <div className="disco-lights">
        {Array.from({ length: 50 }, (_, i) => (
          <div key={i} className={`disco-light disco-${i % 10}`}></div>
        ))}
      </div>
      
      {/* GIANT screen-filling text */}
      <div className="catzy-text-mega">🔥 CATZY! 🔥</div>
      <div className="catzy-text-shadow">CATZY!</div>
      
      {/* Multiple rocket waves */}
      <div className="rocket-dice">
        {Array.from({ length: 25 }, (_, i) => (
          <div key={i} className={`rocket-die rocket-${i % 10}`}>
            <img 
              src={`/cat-images/cat-fly-${(i % 3) + 1}.${i % 3 === 0 ? 'png' : i % 3 === 1 ? 'webp' : 'jpeg'}`}
              alt="Flying cat" 
              className="rocket-cat"
            />
          </div>
        ))}
      </div>
      
      {/* Screen-wide rainbow pulses */}
      <div className="rainbow-pulse-overlay"></div>
      <div className="flash-overlay"></div>
      
      {/* Additional cat chaos */}
      <div className="chaos-cats">
        {Array.from({ length: 75 }, (_, i) => (
          <img 
            key={i}
            src="/cat-images/cat-surprise.png"
            alt="Chaos cat"
            className={`chaos-cat chaos-${i % 15}`}
          />
        ))}
      </div>
    </div>
  );

  const renderFullHouseEffect = () => {
    // Check if specific variant is requested, otherwise cycle through variants
    const specificVariant = effect.variant;
    let effectVariant;
    
    if (specificVariant !== undefined) {
      effectVariant = specificVariant;
    } else {
      effectVariant = (Date.now() / 1000 | 0) % 3;
    }
    
    if (effectVariant === 0) {
      return renderCatMansionBuilder();
    } else if (effectVariant === 1) {
      return renderRoyalCatPalace();
    } else {
      return renderCatCityMetropolis();
    }
  };

  const renderCatMansionBuilder = () => (
    <div className="fullhouse-effect mansion-builder">
      {/* ARCHITECT CATS WITH HARD HATS */}
      <div className="architect-cats">
        {Array.from({ length: 25 }, (_, i) => (
          <div key={i} className={`architect-cat architect-${i % 12}`}>
            <div className="hard-hat">⛑️</div>
            <img 
              src="/cat-images/cat-head-over-table.png" 
              alt="Architect cat" 
              className="architect-cat-image"
            />
            <div className="blueprint">📋</div>
          </div>
        ))}
      </div>
      
      {/* MASSIVE RAINBOW MANSION RISING */}
      <div className="rainbow-mansion">
        <div className="mansion-base">🏰</div>
        <div className="mansion-towers">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className={`mansion-tower tower-${i}`}>🏯</div>
          ))}
        </div>
        <div className="rainbow-roof"></div>
      </div>
      
      {/* FLYING CONSTRUCTION MATERIALS */}
      <div className="flying-materials">
        {Array.from({ length: 40 }, (_, i) => (
          <div key={i} className={`material material-${i % 8}`}>
            {['🧱', '🪵', '⚒️', '🔨', '🪚', '🏗️', '🚧', '⚡'][i % 8]}
          </div>
        ))}
      </div>
      
      {/* DISCO BALLS AND CONFETTI */}
      <div className="disco-construction">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className={`disco-ball disco-${i % 5}`}>🪩</div>
        ))}
      </div>
      
      <div className="construction-confetti">
        {Array.from({ length: 60 }, (_, i) => (
          <div key={i} className={`confetti confetti-${i % 10}`}>🎊</div>
        ))}
      </div>
    </div>
  );

  const renderRoyalCatPalace = () => (
    <div className="fullhouse-effect royal-palace">
      {/* ROYAL CATS WITH CROWNS */}
      <div className="royal-cats">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={`royal-cat royal-${i % 10}`}>
            <div className="crown">👑</div>
            <img 
              src="/cat-images/cat-surprise.png" 
              alt="Royal cat" 
              className="royal-cat-image"
            />
            <div className="royal-robe">👘</div>
          </div>
        ))}
      </div>
      
      {/* GOLDEN PALACE RISING */}
      <div className="golden-palace">
        <div className="palace-base">🏛️</div>
        <div className="palace-columns">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className={`palace-column column-${i}`}>🏛️</div>
          ))}
        </div>
        <div className="golden-dome">🕌</div>
      </div>
      
      {/* SPARKLERS AND GOLD RAIN */}
      <div className="sparklers">
        {Array.from({ length: 30 }, (_, i) => (
          <div key={i} className={`sparkler sparkler-${i % 6}`}>🎆</div>
        ))}
      </div>
      
      {/* RED CARPET UNROLLING */}
      <div className="red-carpet"></div>
      
      {/* PAPARAZZI CATS */}
      <div className="paparazzi-cats">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`paparazzi-cat paparazzi-${i % 6}`}>
            <img 
              src="/cat-images/cat-back.png" 
              alt="Paparazzi cat" 
              className="paparazzi-cat-image"
            />
            <div className="camera-flash">📸</div>
          </div>
        ))}
      </div>
      
      {/* GOLD COINS FALLING */}
      <div className="gold-rain">
        {Array.from({ length: 50 }, (_, i) => (
          <div key={i} className={`gold-coin coin-${i % 15}`}>🪙</div>
        ))}
      </div>
    </div>
  );

  const renderCatCityMetropolis = () => (
    <div className="fullhouse-effect city-metropolis">
      {/* SKYSCRAPERS MADE OF CAT EMOJIS */}
      <div className="cat-skyscrapers">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className={`skyscraper building-${i}`}>
            {Array.from({ length: 15 }, (_, j) => (
              <div key={j} className={`cat-floor floor-${j}`}>
                🐱🐱🐱
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* HELICOPTER CATS */}
      <div className="helicopter-cats">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className={`helicopter-cat heli-${i % 5}`}>
            <div className="helicopter">🚁</div>
            <img 
              src="/cat-images/cat-fly-2.webp" 
              alt="Helicopter cat" 
              className="helicopter-cat-image"
            />
            <div className="rainbow-banner">FULL HOUSE!</div>
          </div>
        ))}
      </div>
      
      {/* CITY LIGHTS TWINKLING */}
      <div className="city-lights">
        {Array.from({ length: 100 }, (_, i) => (
          <div key={i} className={`city-light light-${i % 20}`}>💡</div>
        ))}
      </div>
      
      {/* FIREWORK EXPLOSIONS */}
      <div className="city-fireworks">
        {Array.from({ length: 25 }, (_, i) => (
          <div key={i} className={`city-firework explosion-${i % 8}`}>
            🎆
          </div>
        ))}
      </div>
      
      {/* FLYING CARS WITH CATS */}
      <div className="flying-cars">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className={`flying-car car-${i}`}>
            🚗
            <img 
              src="/cat-images/cat-high-5.png" 
              alt="Driving cat" 
              className="driving-cat"
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderStraightEffect = () => {
    // Check if specific variant is requested
    const specificVariant = effect.variant;
    const isLargeStraight = effect.length === 5 || effect.size === 'large'; // Support both length and size properties
    
    if (specificVariant !== undefined) {
      // Specific variant requested (0-3, where 0-1 are small, 2-3 are large)
      if (specificVariant === 0) {
        return renderNeonRacetrack();
      } else if (specificVariant === 1) {
        return renderCatSurfPipeline();
      } else if (specificVariant === 2) {
        return renderSpaceRaceOdyssey();
      } else if (specificVariant === 3) {
        return renderTimeWarpPortal();
      }
    }
    
    // Default behavior - cycle through variants based on straight size
    const effectVariant = (Date.now() / 1000 | 0) % 2;
    
    if (isLargeStraight) {
      if (effectVariant === 0) {
        return renderSpaceRaceOdyssey();
      } else {
        return renderTimeWarpPortal();
      }
    } else {
      if (effectVariant === 0) {
        return renderNeonRacetrack();
      } else {
        return renderCatSurfPipeline();
      }
    }
  };

  // SMALL STRAIGHT EFFECTS
  const renderNeonRacetrack = () => (
    <div className="straight-effect neon-racetrack">
      {/* NEON RACE CARS WITH CATS */}
      <div className="neon-racecars">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`neon-racecar racer-${i % 6}`}>
            <div className="neon-car">🏎️</div>
            <img 
              src="/cat-images/cat-high-5.png" 
              alt="Racing cat" 
              className="racing-cat-image"
            />
            <div className="speed-trail"></div>
          </div>
        ))}
      </div>
      
      {/* MASSIVE NEON TRACK */}
      <div className="neon-track-system">
        <div className="track-segment track-1"></div>
        <div className="track-segment track-2"></div>
        <div className="track-segment track-3"></div>
        <div className="track-segment track-4"></div>
      </div>
      
      {/* HOLOGRAPHIC CHECKERED FLAGS */}
      <div className="checkered-flags">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={`checkered-flag flag-${i % 8}`}>🏁</div>
        ))}
      </div>
      
      {/* ELECTRIC LIGHTNING BOLTS */}
      <div className="electric-lightning">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className={`lightning-bolt bolt-${i % 5}`}>⚡</div>
        ))}
      </div>
      
      {/* SYNTHWAVE GRID */}
      <div className="synthwave-grid"></div>
    </div>
  );

  const renderCatSurfPipeline = () => (
    <div className="straight-effect cat-surf-pipeline">
      {/* MASSIVE WAVE TUNNEL */}
      <div className="wave-tunnel">
        <div className="wave-curl"></div>
        <div className="wave-foam"></div>
      </div>
      
      {/* SURFING CATS ON BOARDS */}
      <div className="surfing-cats">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className={`surfing-cat surfer-${i % 4}`}>
            <div className="surfboard">🏄</div>
            <img 
              src="/cat-images/cat-fly-1.png" 
              alt="Surfing cat" 
              className="surfer-cat-image"
            />
            <div className="water-splash">💦</div>
          </div>
        ))}
      </div>
      
      {/* TROPICAL ELEMENTS */}
      <div className="tropical-vibes">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className={`tropical-element tropical-${i % 5}`}>
            {['🌴', '🥥', '🌺', '🐚', '🦜'][i % 5]}
          </div>
        ))}
      </div>
      
      {/* DOLPHINS JUMPING */}
      <div className="jumping-dolphins">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className={`dolphin dolphin-${i}`}>🐬</div>
        ))}
      </div>
      
      {/* SUN RAYS */}
      <div className="sun-rays">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`sun-ray ray-${i}`}></div>
        ))}
      </div>
    </div>
  );

  // LARGE STRAIGHT EFFECTS  
  const renderSpaceRaceOdyssey = () => (
    <div className="straight-effect space-race-odyssey">
      {/* SPACE SHUTTLE CATS */}
      <div className="space-shuttles">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className={`space-shuttle shuttle-${i % 4}`}>
            <div className="shuttle">🚀</div>
            <img 
              src="/cat-images/cat-fly-3.jpeg" 
              alt="Astronaut cat" 
              className="astronaut-cat-image"
            />
            <div className="rocket-flames">🔥</div>
          </div>
        ))}
      </div>
      
      {/* GALAXY SPIRAL */}
      <div className="galaxy-spiral">
        <div className="spiral-arm arm-1"></div>
        <div className="spiral-arm arm-2"></div>
        <div className="spiral-arm arm-3"></div>
        <div className="spiral-arm arm-4"></div>
        <div className="galaxy-center">🌌</div>
      </div>
      
      {/* PLANETS AND STARS */}
      <div className="cosmic-objects">
        {Array.from({ length: 30 }, (_, i) => (
          <div key={i} className={`cosmic-object cosmic-${i % 10}`}>
            {['🪐', '⭐', '🌟', '🌙', '☄️', '🛸', '👽', '🌍', '🌑', '💫'][i % 10]}
          </div>
        ))}
      </div>
      
      {/* WORMHOLE PORTAL */}
      <div className="wormhole-portal">
        <div className="portal-ring ring-1"></div>
        <div className="portal-ring ring-2"></div>
        <div className="portal-ring ring-3"></div>
        <div className="portal-center"></div>
      </div>
      
      {/* NEBULA CLOUDS */}
      <div className="nebula-clouds">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className={`nebula-cloud cloud-${i}`}></div>
        ))}
      </div>
    </div>
  );

  const renderTimeWarpPortal = () => (
    <div className="straight-effect time-warp-portal">
      {/* TIME TRAVELING CATS */}
      <div className="time-traveling-cats">
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className={`time-traveler traveler-${i % 5}`}>
            <div className="time-machine">⏰</div>
            <img 
              src="/cat-images/cat-surprise.png" 
              alt="Time traveler cat" 
              className="time-traveler-cat-image"
            />
            <div className="chronon-trail"></div>
          </div>
        ))}
      </div>
      
      {/* MASSIVE CLOCK GEARS */}
      <div className="clock-gears">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`clock-gear gear-${i % 6}`}>⚙️</div>
        ))}
      </div>
      
      {/* TIME PORTALS */}
      <div className="time-portals">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className={`time-portal portal-${i}`}>
            <div className="portal-frame">🌀</div>
            <div className="temporal-energy"></div>
          </div>
        ))}
      </div>
      
      {/* FLOATING HOURGLASSES */}
      <div className="floating-hourglasses">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className={`hourglass hourglass-${i % 8}`}>⏳</div>
        ))}
      </div>
      
      {/* TEMPORAL LIGHTNING */}
      <div className="temporal-lightning">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={`temporal-bolt t-bolt-${i % 10}`}></div>
        ))}
      </div>
      
      {/* CALENDAR PAGES FLYING */}
      <div className="flying-calendar-pages">
        {Array.from({ length: 25 }, (_, i) => (
          <div key={i} className={`calendar-page page-${i % 12}`}>📅</div>
        ))}
      </div>
    </div>
  );

  const renderKindEffect = () => {
    // Check if specific variant is requested
    const specificVariant = effect.variant;
    const kindCount = effect.count;
    
    if (specificVariant !== undefined) {
      // Specific variant requested (0-5, where 0-2 are 3-of-a-kind, 3-5 are 4-of-a-kind)
      if (kindCount === 3) {
        if (specificVariant === 0) {
          return renderCatCloneArmy();
        } else if (specificVariant === 1) {
          return renderMagicMirrorMaze();
        } else if (specificVariant === 2) {
          return renderTripleTrouble();
        }
      } else if (kindCount === 4) {
        if (specificVariant === 3) {
          return renderCatCarnivalExtravaganza();
        } else if (specificVariant === 4) {
          return renderElementalCatForce();
        } else if (specificVariant === 5) {
          return renderCyberCatMatrix();
        }
      }
    }
    
    // Default behavior - cycle through variants based on count
    const effectVariant = (Date.now() / 1000 | 0) % 3;
    
    if (kindCount === 3) {
      if (effectVariant === 0) {
        return renderCatCloneArmy();
      } else if (effectVariant === 1) {
        return renderMagicMirrorMaze();
      } else {
        return renderTripleTrouble();
      }
    } else if (kindCount === 4) {
      if (effectVariant === 0) {
        return renderCatCarnivalExtravaganza();
      } else if (effectVariant === 1) {
        return renderElementalCatForce();
      } else {
        return renderCyberCatMatrix();
      }
    }
    
    // Fallback to original effect
    return renderOriginalKindEffect();
  };

  // ORIGINAL KIND EFFECT (fallback)
  const renderOriginalKindEffect = () => (
    <div className="kind-effect">
      <div className="pulsing-dice">
        {Array.from({ length: effect.count }, (_, i) => (
          <div key={i} className={`pulsing-die pulse-${i}`}>
            {effect.emoji}
          </div>
        ))}
      </div>
      <div className="clone-cats">
        {Array.from({ length: effect.count }, (_, i) => (
          <div key={i} className={`clone-cat clone-${i}`}>
            <img 
              src="/cat-images/cat-surprise.png" 
              alt="Clone cat" 
              className="clone-cat-image"
            />
          </div>
        ))}
      </div>
    </div>
  );

  // THREE OF A KIND EFFECTS
  const renderCatCloneArmy = () => (
    <div className="kind-effect cat-clone-army">
      {/* MASSIVE ARMY OF CLONE CATS MARCHING */}
      <div className="clone-army-formation">
        {Array.from({ length: 30 }, (_, i) => (
          <div key={i} className={`army-clone army-${i % 15}`}>
            <div className="army-helmet">🪖</div>
            <img 
              src="/cat-images/cat-high-5.png" 
              alt="Army clone cat" 
              className="army-clone-image"
            />
            <div className="march-dust">💨</div>
          </div>
        ))}
      </div>
      
      {/* DICE FORTRESS */}
      <div className="dice-fortress">
        <div className="fortress-base">{effect.emoji}</div>
        <div className="fortress-towers">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className={`fortress-tower tower-${i}`}>{effect.emoji}</div>
          ))}
        </div>
      </div>
      
      {/* MILITARY FLAGS */}
      <div className="military-flags">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`military-flag flag-${i % 6}`}>🚩</div>
        ))}
      </div>
      
      {/* BATTLE EXPLOSIONS */}
      <div className="battle-explosions">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={`battle-explosion explosion-${i % 10}`}>💥</div>
        ))}
      </div>
    </div>
  );

  const renderMagicMirrorMaze = () => (
    <div className="kind-effect magic-mirror-maze">
      {/* FLOATING MAGIC MIRRORS */}
      <div className="magic-mirrors">
        {Array.from({ length: 18 }, (_, i) => (
          <div key={i} className={`magic-mirror mirror-${i % 9}`}>
            <div className="mirror-frame">🪞</div>
            <div className="mirror-reflection">
              <img 
                src="/cat-images/cat-surprise.png" 
                alt="Mirror cat" 
                className="mirror-cat-image"
              />
            </div>
            <div className="magic-sparkles">✨</div>
          </div>
        ))}
      </div>
      
      {/* CRYSTAL DICE PEDESTALS */}
      <div className="crystal-pedestals">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className={`crystal-pedestal pedestal-${i}`}>
            <div className="crystal-base">💎</div>
            <div className="hovering-dice">{effect.emoji}</div>
            <div className="magic-aura"></div>
          </div>
        ))}
      </div>
      
      {/* MYSTICAL PORTALS */}
      <div className="mystical-portals">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className={`mystical-portal portal-${i % 4}`}>
            <div className="portal-ring">🔮</div>
            <div className="portal-energy"></div>
          </div>
        ))}
      </div>
      
      {/* FLOATING SPELL BOOKS */}
      <div className="spell-books">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className={`spell-book book-${i % 8}`}>📖</div>
        ))}
      </div>
    </div>
  );

  const renderTripleTrouble = () => (
    <div className="kind-effect triple-trouble">
      {/* TROUBLEMAKER CATS WITH MISCHIEF */}
      <div className="troublemaker-cats">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className={`troublemaker-cat trouble-${i % 8}`}>
            <div className="mischief-hat">🎩</div>
            <img 
              src="/cat-images/cat-back.png" 
              alt="Troublemaker cat" 
              className="troublemaker-cat-image"
            />
            <div className="chaos-swirl">🌪️</div>
          </div>
        ))}
      </div>
      
      {/* SPINNING DICE TORNADO */}
      <div className="dice-tornado">
        <div className="tornado-base"></div>
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`tornado-dice dice-${i % 6}`}>{effect.emoji}</div>
        ))}
      </div>
      
      {/* PRANK EXPLOSIONS */}
      <div className="prank-explosions">
        {Array.from({ length: 25 }, (_, i) => (
          <div key={i} className={`prank-explosion prank-${i % 12}`}>
            {['🎉', '🎊', '🎈', '🎁', '🧨', '💫', '⭐', '🌟', '✨', '🎪', '🎭', '🃏'][i % 12]}
          </div>
        ))}
      </div>
      
      {/* JESTER HATS FALLING */}
      <div className="falling-jester-hats">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={`jester-hat hat-${i % 10}`}>🤹</div>
        ))}
      </div>
    </div>
  );

  // FOUR OF A KIND EFFECTS
  const renderCatCarnivalExtravaganza = () => (
    <div className="kind-effect cat-carnival-extravaganza">
      {/* MASSIVE FERRIS WHEEL WITH CATS IN EACH CAR */}
      <div className="carnival-ferris-wheel">
        <div className="ferris-wheel-center">🎡</div>
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className={`ferris-car car-${i}`}>
            <div className="carnival-car">🎠</div>
            <img 
              src="/cat-images/cat-high-5.png" 
              alt="Carnival cat" 
              className="carnival-cat-image"
            />
            <div className="carnival-lights">💡</div>
          </div>
        ))}
      </div>
      
      {/* CIRCUS TENT WITH FLYING TRAPEZE CATS */}
      <div className="circus-tent">
        <div className="big-top">🎪</div>
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className={`trapeze-cat trapeze-${i % 3}`}>
            <div className="trapeze-bar">🤸</div>
            <img 
              src="/cat-images/cat-fly-1.png" 
              alt="Trapeze cat" 
              className="trapeze-cat-image"
            />
            <div className="safety-net">🕷️</div>
          </div>
        ))}
      </div>
      
      {/* COTTON CANDY AND POPCORN STANDS */}
      <div className="carnival-treats">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`carnival-treat treat-${i % 6}`}>
            {['🍿', '🍭', '🎂', '🧁', '🍯', '🎈'][i % 6]}
          </div>
        ))}
      </div>
      
      {/* DICE ROLLER COASTER TRACK */}
      <div className="dice-roller-coaster">
        <div className="coaster-track"></div>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={`coaster-car coaster-${i}`}>
            <div className="dice-car">{effect.emoji}</div>
            <img 
              src="/cat-images/cat-surprise.png" 
              alt="Coaster cat" 
              className="coaster-cat-image"
            />
          </div>
        ))}
      </div>
      
      {/* CARNIVAL FIREWORKS */}
      <div className="carnival-fireworks">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={`carnival-firework firework-${i % 10}`}>
            🎆
          </div>
        ))}
      </div>
      
      {/* BALLOON ANIMALS */}
      <div className="balloon-animals">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className={`balloon-animal balloon-${i % 8}`}>
            {['🎈', '🐶', '🐘', '🦒', '🐒', '🦁', '🐯', '🐻'][i % 8]}
          </div>
        ))}
      </div>
    </div>
  );

  const renderElementalCatForce = () => (
    <div className="kind-effect elemental-cat-force">
      {/* FOUR ELEMENTAL CATS */}
      <div className="elemental-cats">
        <div className="fire-cat elemental-corner corner-0">
          <div className="element-symbol">🔥</div>
          <img 
            src="/cat-images/cat-fly-1.png" 
            alt="Fire cat" 
            className="elemental-cat-image fire-glow"
          />
          <div className="fire-waves"></div>
        </div>
        <div className="water-cat elemental-corner corner-1">
          <div className="element-symbol">🌊</div>
          <img 
            src="/cat-images/cat-fly-2.webp" 
            alt="Water cat" 
            className="elemental-cat-image water-glow"
          />
          <div className="water-ripples"></div>
        </div>
        <div className="earth-cat elemental-corner corner-2">
          <div className="element-symbol">🌍</div>
          <img 
            src="/cat-images/cat-head-over-table.png" 
            alt="Earth cat" 
            className="elemental-cat-image earth-glow"
          />
          <div className="earth-crystals"></div>
        </div>
        <div className="air-cat elemental-corner corner-3">
          <div className="element-symbol">🌪️</div>
          <img 
            src="/cat-images/cat-fly-3.jpeg" 
            alt="Air cat" 
            className="elemental-cat-image air-glow"
          />
          <div className="air-swirls"></div>
        </div>
      </div>
      
      {/* ELEMENTAL CONVERGENCE CENTER */}
      <div className="elemental-convergence">
        <div className="convergence-dice">{effect.emoji}</div>
        <div className="elemental-fusion"></div>
      </div>
      
      {/* ELEMENTAL PARTICLES */}
      <div className="elemental-particles">
        {Array.from({ length: 40 }, (_, i) => (
          <div key={i} className={`elemental-particle particle-${i % 20}`}>
            {['🔥', '💧', '🌿', '💨', '⚡', '❄️', '🌋', '🌈'][i % 8]}
          </div>
        ))}
      </div>
    </div>
  );

  const renderCyberCatMatrix = () => (
    <div className="kind-effect cyber-cat-matrix">
      {/* DIGITAL GRID BACKGROUND */}
      <div className="digital-grid"></div>
      
      {/* HACKER CATS WITH VR GOGGLES */}
      <div className="hacker-cats">
        {Array.from({ length: 16 }, (_, i) => (
          <div key={i} className={`hacker-cat hacker-${i % 8}`}>
            <div className="vr-goggles">🥽</div>
            <img 
              src="/cat-images/cat-back.png" 
              alt="Hacker cat" 
              className="hacker-cat-image"
            />
            <div className="code-stream"></div>
          </div>
        ))}
      </div>
      
      {/* HOLOGRAPHIC DICE CUBES */}
      <div className="holographic-cubes">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={`holo-cube cube-${i}`}>
            <div className="holo-dice">{effect.emoji}</div>
            <div className="holo-wireframe"></div>
          </div>
        ))}
      </div>
      
      {/* MATRIX CODE RAIN */}
      <div className="matrix-code-rain">
        {Array.from({ length: 30 }, (_, i) => (
          <div key={i} className={`code-column column-${i % 15}`}>
            {Array.from({ length: 10 }, (_, j) => (
              <div key={j} className="code-char">{Math.random() > 0.5 ? '1' : '0'}</div>
            ))}
          </div>
        ))}
      </div>
      
      {/* NEON CIRCUITS */}
      <div className="neon-circuits">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={`neon-circuit circuit-${i % 10}`}></div>
        ))}
      </div>
      
      {/* GLITCH EFFECTS */}
      <div className="glitch-effects">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`glitch glitch-${i % 6}`}></div>
        ))}
      </div>
    </div>
  );

  const renderAchievementEffect = () => (
    <div className="achievement-effect">
      <div className="achievement-banner">
        <div className="achievement-text">🏆 ACHIEVEMENT UNLOCKED! 🏆</div>
        <div className="achievement-description">{effect.description}</div>
        <div className="dj-cat">
          <img 
            src="/cat-images/cat-back.png" 
            alt="DJ cat" 
            className="dj-cat-image"
          />
        </div>
      </div>
      <div className="glow-sticks">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`glow-stick glow-${i % 4}`}></div>
        ))}
      </div>
    </div>
  );

  switch (effect.type) {
    case 'yahtzee':
      return renderYahtzeeEffect();
    case 'fullhouse':
      return renderFullHouseEffect();
    case 'straight':
      return renderStraightEffect();
    case 'kind':
      return renderKindEffect();
    case 'achievement':
      return renderAchievementEffect();
    default:
      return null;
  }
};

export default EffectsOverlay;